import aplicativoIcon from '@cannect/assets/img/home/cta-icons/aplicativo.webp'
import appStoreBadge from '@cannect/assets/img/botao_donwload_apple.png'
import playStoreBadge from '@cannect/assets/img/botao_donwload_playstore.png'
import appBackground from '@cannect/assets/img/home/app-showcase-bg.webp'
import { Typography } from '@cannect/new-components/atoms'
import { CtaPill } from '@cannect/new-components/molecules'
import { LuCheck } from 'react-icons/lu'

const APP_LANDING_URL = 'https://atendimento.cannect.life/app-cannect'

const APP_FEATURES = ['Consultas e agenda', 'Renovação e recompra', 'Pedidos e documentos', 'Próxima etapa do cuidado']

type TAppShowcaseProps = {
  onAndroidClick?: () => void
  onIOSClick?: () => void
}

/**
 * Seção "Aplicativo Cannect" (Figma node 4161:23405).
 *
 * O mockup do iPhone vem no próprio fundo, não como elemento separado. No design a imagem é
 * escalada a 140,49% da largura e deslocada — transformação travada na proporção do desktop, então
 * o asset já foi recortado na janela visível (proporção 2,048, igual à da seção) e aqui basta
 * `object-cover`. O PNG original tinha 880KB; recortado e convertido para WebP ficou em 24KB.
 *
 * Os selos de loja recebem handlers por prop porque a Home dispara eventos de conversão no gtag
 * antes de abrir a store — esses handlers existiam e estavam sem uso desde a home anterior.
 */
export const AppShowcase = ({ onAndroidClick, onIOSClick }: TAppShowcaseProps) => (
  <section aria-labelledby="app-showcase-heading" className="relative w-full overflow-hidden bg-marketplace-800">
    <img
      src={appBackground}
      alt=""
      aria-hidden
      loading="lazy"
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover object-center"
    />

    {/* 74px: no arquivo a coluna de conteúdo (node 4333:1420) fica 10px dentro do frame da
          seção, que por sua vez tem 64px de padding. */}
    <div className="relative mx-auto w-full max-w-[1230px] px-8 py-[74px]">
      <div className="flex max-w-[617px] flex-col gap-12">
        <div className="flex flex-col gap-2">
          <p className="m-0 text-base leading-[1.6] text-neutral-50">Baixe grátis o APP Cannect</p>

          <div className="flex flex-col gap-6">
            {/* H2 de 36px, como no "Jornada segura e confiável" e no blog — não os 28px das demais
                seções. Medida do arquivo (node 4333:1426). */}
            <Typography.Heading
              id="app-showcase-heading"
              level={2}
              weight="regular"
              className="m-0 text-[36px] leading-[49.28px] tracking-[-0.448px] text-neutral-50">
              {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica. */}
              Sua saúde na <span className="italic">palma da mão</span>
            </Typography.Heading>

            <p className="m-0 text-base font-light leading-[1.4] text-neutral-50">
              Acompanhe pedidos, consultas, documentos e próximas etapas sem precisar recomeçar a cada interação.
            </p>
          </div>
        </div>

        <ul className="m-0 flex list-none flex-wrap gap-[13px] p-0">
          {APP_FEATURES.map((feature) => (
            <li
              key={feature}
              className="flex w-[302px] max-w-full items-center gap-[10px] rounded-[8px] bg-secondary-100/20 p-6">
              <span
                aria-hidden
                className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-marketplace-500">
                <LuCheck className="h-[10px] w-[10px] text-marketplace-800" strokeWidth={3} />
              </span>
              <span className="text-xs font-semibold leading-[18.2px] text-neutral-50">{feature}</span>
            </li>
          ))}
        </ul>

        <CtaPill
          variant="solidDeep"
          title="Conhecer o aplicativo"
          path={APP_LANDING_URL}
          external
          icon={aplicativoIcon}
          className="w-fit self-start"
        />

        <div className="flex flex-wrap items-start gap-3">
          <button
            type="button"
            onClick={onAndroidClick}
            className="cursor-pointer appearance-none border-0 bg-transparent p-0 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent">
            <img src={playStoreBadge} alt="Disponível no Google Play" className="h-[47px] w-auto" />
          </button>

          <button
            type="button"
            onClick={onIOSClick}
            className="cursor-pointer appearance-none border-0 bg-transparent p-0 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent">
            <img src={appStoreBadge} alt="Baixar na App Store" className="h-[47px] w-auto" />
          </button>
        </div>
      </div>
    </div>
  </section>
)
